(() => {
  const DIGIT_INTERVAL = 80;
  const RESOLVE_START = 420;
  const RESOLVE_SPREAD = 660;
  const MAX_FRAME_GAP = 180;
  const SETTLE_TIME = 260;
  const graphemes = typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
  // Boundaries between the five original SVG paths: R, S, 7, G and T.
  const LOGO_CUTS = [0, 18.7, 38.4, 58.3, 82, 100];
  const LOGO_CENTERS = [9.124, 28.109, 47.149, 71.597, 91.506];

  window.RS7GTClockReveal = class {
    constructor(root) {
      this.root = root;
      this.controller = new AbortController();
      this.items = new Map();
      this.active = new Set();
      this.pending = new Set();
      this.ready = false;
      this.frame = 0;
      this.tick = this.tick.bind(this);
      const options = { signal: this.controller.signal };
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.active.forEach(item => { item.paused = true; });
          cancelAnimationFrame(this.frame);
          this.frame = 0;
        } else this.wake();
      }, options);
      window.addEventListener('scroll', () => this.wake(), { ...options, passive: true });
      window.addEventListener('resize', () => this.wake(), options);
      this.root.addEventListener('rs7gt:pagecoveragechange', () => this.wake(), options);
      ['toggle', 'transitionend', 'focusin'].forEach(name => {
        this.root.addEventListener(name, () => this.wake(), { ...options, capture: true });
      });
      this.initialize().catch(() => this.destroy());
    }

    async initialize() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const item = this.items.get(entry.target);
            if (!item) return;
            if (!entry.target.isConnected) this.discard(item);
            else {
              item.inView = entry.isIntersecting && entry.intersectionRatio >= 0.3;
              if (!item.inView && item.started) item.paused = true;
            }
          });
          this.wake();
        }, { threshold: [0, 0.3], rootMargin: '0px 0px -10% 0px' });
      }
      // Prepare synchronously: upcoming text cannot flash in full while fonts
      // settle. A bounded wait restores the readable fallback if fonts stall.
      this.root.querySelectorAll('[data-clock-reveal], [data-clock-logo]').forEach(element => this.register(element));
      const fontsReady = await Promise.race([
        Promise.resolve(document.fonts?.ready).then(() => true),
        new Promise(resolve => { this.fontTimer = setTimeout(() => resolve(false), 1500); })
      ]);
      clearTimeout(this.fontTimer);
      if (this.controller.signal.aborted) return;
      if (!fontsReady) {
        this.destroy();
        return;
      }
      this.ready = true;
      this.wake();
    }

    register(element) {
      if (this.controller.signal.aborted || !element || !this.root.contains(element)
        || element.hasAttribute('data-clock-complete')
        || element.closest('rs7gt-launch-clock, .rs7-launch-sr, .rs7-clock-source, .sr-only, .visually-hidden')) return null;
      if (this.items.has(element)) return this.items.get(element);
      const manual = element.hasAttribute('data-clock-manual');
      const item = { element, logo: element.hasAttribute('data-clock-logo'), requested: !manual,
        prepareEarly: !manual || !!(window.gsap && window.ScrollTrigger) };
      this.items.set(element, item);
      if (item.logo) {
        if (item.prepareEarly) element.classList.add('is-clock-prepared');
        this.observeLogo(item);
      } else if (item.prepareEarly) this.prepare(item);
      this.observer?.observe(element);
      if (item.requested) this.pending.add(item);
      this.wake();
      return item;
    }

    setText(element, text) {
      if (!element || !this.root.contains(element)) return;
      const value = String(text ?? '');
      const item = this.items.get(element);
      const current = item?.originalNodes
        ? item.originalNodes.map(node => node.textContent).join('') : element.textContent;
      if (current === value) {
        if (!item && !element.hasAttribute('data-clock-complete')) this.register(element);
        return;
      }
      if (item) this.discard(item);
      element.textContent = value;
      element.removeAttribute('data-clock-complete');
      element.setAttribute('data-clock-reveal', '');
      this.reveal(element);
    }

    reveal(element) {
      if (this.controller.signal.aborted || !element || !this.root.contains(element)
        || element.hasAttribute('data-clock-complete')) return;
      const item = this.items.get(element) || this.register(element);
      if (!item) return;
      if (item.started) {
        this.wake();
        return;
      }
      item.requested = true;
      this.pending.add(item);
      if (!item.logo || item.image) this.prepare(item);
      this.wake();
    }

    async observeLogo(item) {
      const image = item.element.querySelector('img');
      if (!image) {
        this.finish(item);
        return;
      }
      try {
        await Promise.race([image.decode(), new Promise(resolve => {
          item.decodeTimer = setTimeout(resolve, 1500);
        })]);
      } catch (_) {
        // The original image remains the fallback when it cannot be decoded.
      }
      clearTimeout(item.decodeTimer);
      if (this.controller.signal.aborted || this.items.get(item.element) !== item) return;
      if (!image.naturalWidth) {
        this.finish(item);
        return;
      }
      item.image = image;
      if (item.prepareEarly || item.requested) this.prepare(item);
      this.wake();
    }

    prepare(item) {
      if (item.glyphs) return;
      item.glyphs = item.logo ? this.prepareLogo(item) : this.prepareText(item);
      if (item.glyphs.length) item.element.classList.add('is-clock-prepared');
    }

    start(item) {
      if (item.started || !item.element.isConnected) return;
      item.started = true;
      this.pending.delete(item);
      this.prepare(item);
      if (!item.glyphs.length) {
        this.finish(item);
        return;
      }
      const now = performance.now();
      item.elapsed = 0;
      item.lastTick = -1;
      item.lastFrameAt = now;
      item.glyphs.forEach(glyph => glyph.element.classList.add('is-active'));
      this.active.add(item);
    }

    wake() {
      if (!this.ready || this.controller.signal.aborted || document.hidden || this.frame
        || (!this.pending.size && !this.active.size)) return;
      this.frame = requestAnimationFrame(this.tick);
    }

    visibility(item) {
      const element = item.element;
      if (!element.isConnected || (item.logo && !item.image)
        || element.closest('[hidden], [inert]') || (this.root.sceneVisibility?.(element) ?? 1) <= 0) return 0;
      const bounds = element.getBoundingClientRect();
      if (!bounds.width || !bounds.height || bounds.bottom <= 0 || bounds.top >= window.innerHeight
        || bounds.right <= 0 || bounds.left >= window.innerWidth) return 0;
      let opacity = 1;
      for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
        const style = getComputedStyle(ancestor);
        if (style.visibility === 'hidden' || style.display === 'none') return 0;
        opacity *= Number(style.opacity);
        if (ancestor === this.root) break;
      }
      if (opacity < 0.05) return -1;
      const x = Math.max(0, Math.min(window.innerWidth - 1, (Math.max(0, bounds.left) + Math.min(window.innerWidth, bounds.right)) / 2));
      const y = Math.max(0, Math.min(window.innerHeight - 1, (Math.max(0, bounds.top) + Math.min(window.innerHeight, bounds.bottom)) / 2));
      const hit = document.elementFromPoint(x, y);
      const scene = element.closest('#MainContent > .shopify-section');
      if (hit && scene && hit.closest('#MainContent > .shopify-section') !== scene) return 0;
      if (hit && !scene && !element.contains(hit) && !hit.contains(element)
        && !hit.closest('a, button, summary, label')?.contains(element)) return 0;
      return 1;
    }

    prepareText(item) {
      const text = item.element.textContent;
      if (!text.trim() || item.element.querySelector('a, button, input, select, textarea')) return [];
      item.originalNodes = [...item.element.childNodes];
      const source = document.createElement('span');
      source.className = 'rs7-clock-source';
      const visual = document.createElement('span');
      visual.className = 'rs7-clock-text';
      visual.setAttribute('aria-hidden', 'true');
      const glyphs = [];

      const appendVisual = (node, parent) => {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent.split(/(\s+)/).forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              parent.append(document.createTextNode(part));
              return;
            }
            const word = document.createElement('span');
            word.className = 'rs7-clock-word';
            const characters = graphemes ? [...graphemes.segment(part)].map(item => item.segment)
              : Array.from(part.normalize('NFC'));
            characters.forEach(character => {
              const glyph = document.createElement('span');
              glyph.className = 'rs7-clock-glyph';
              const final = document.createElement('span');
              final.className = 'rs7-clock-glyph__final';
              final.textContent = character;
              const digit = document.createElement('span');
              digit.className = 'rs7-clock-glyph__digit';
              digit.textContent = String(glyphs.length * 3 % 10);
              glyph.append(final, digit);
              word.append(glyph);
              glyphs.push({ element: glyph, digit });
            });
            parent.append(word);
          });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const clone = node.cloneNode(false);
          clone.removeAttribute('id');
          node.childNodes.forEach(child => appendVisual(child, clone));
          parent.append(clone);
        }
      };
      item.originalNodes.forEach(node => appendVisual(node, visual));
      // Keep the real nodes (including emphasis and line breaks) accessible and
      // restore the same nodes when done, without exposing the changing digits.
      source.append(...item.originalNodes);
      item.element.replaceChildren(source, visual);
      return glyphs;
    }

    prepareLogo(item) {
      const overlay = document.createElement('span');
      overlay.className = 'rs7-clock-logo__overlay';
      overlay.setAttribute('aria-hidden', 'true');
      const glyphs = LOGO_CENTERS.map((center, index) => {
        const left = LOGO_CUTS[index];
        const width = LOGO_CUTS[index + 1] - left;
        const cell = document.createElement('span');
        cell.className = 'rs7-clock-logo__cell';
        cell.style.setProperty('--clock-left', `${left}%`);
        cell.style.setProperty('--clock-width', `${width}%`);
        cell.style.setProperty('--clock-digit-left', `${(center - left) / width * 100}%`);
        const final = item.image.cloneNode(false);
        final.classList.add('rs7-clock-logo__final');
        final.removeAttribute('id');
        final.alt = '';
        final.style.width = `${10000 / width}%`;
        final.style.left = `${-left / width * 100}%`;
        const digit = document.createElement('span');
        digit.className = 'rs7-clock-logo__digit';
        digit.textContent = String(index * 3 % 10);
        cell.append(final, digit);
        overlay.append(cell);
        return { element: cell, digit };
      });
      item.overlay = overlay;
      item.element.append(overlay);
      return glyphs;
    }

    tick() {
      this.frame = 0;
      if (this.controller.signal.aborted) return;
      if (document.hidden) {
        this.active.forEach(item => { item.paused = true; });
        return;
      }
      const now = performance.now();
      let needsFrame = false;
      this.pending.forEach(item => {
        if (!item.element.isConnected) {
          this.discard(item);
          return;
        }
        const visible = this.visibility(item);
        if (visible > 0) this.start(item);
        else if (visible < 0) needsFrame = true;
      });
      this.active.forEach(item => {
        if (!item.element.isConnected) {
          this.discard(item);
          return;
        }
        const visible = this.visibility(item);
        if (visible !== 1) {
          item.paused = true;
          if (visible < 0) needsFrame = true;
          return;
        }
        needsFrame = true;
        // Keep the original short reveal while preserving pauses for covered
        // scenes and background tabs.
        if (item.paused || now - item.lastFrameAt > MAX_FRAME_GAP) {
          item.paused = false;
          item.lastFrameAt = now;
          return;
        }
        item.elapsed += Math.max(0, now - item.lastFrameAt);
        item.lastFrameAt = now;
        if (item.elapsed >= RESOLVE_START + RESOLVE_SPREAD + SETTLE_TIME) {
          this.finish(item);
          return;
        }
        const tick = Math.floor(item.elapsed / DIGIT_INTERVAL);
        if (tick === item.lastTick) return;
        item.lastTick = tick;
        item.glyphs.forEach((glyph, index) => {
          if (glyph.resolved) return;
          const progress = index / Math.max(1, item.glyphs.length - 1);
          if (item.elapsed >= RESOLVE_START + progress * RESOLVE_SPREAD) {
            glyph.resolved = true;
            glyph.element.classList.remove('is-active');
            glyph.element.classList.add('is-resolved');
          } else glyph.digit.textContent = String((tick + index * 3) % 10);
        });
      });
      if (needsFrame && (this.active.size || this.pending.size)) this.frame = requestAnimationFrame(this.tick);
    }

    finish(item) {
      this.discard(item);
      item.element.setAttribute('data-clock-complete', '');
    }

    discard(item) {
      clearTimeout(item.decodeTimer);
      this.active.delete(item);
      this.pending.delete(item);
      this.observer?.unobserve(item.element);
      this.items.delete(item.element);
      this.restore(item);
      if (!this.active.size && !this.pending.size) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
    }

    restore(item) {
      if (item.originalNodes) item.element.replaceChildren(...item.originalNodes);
      item.overlay?.remove();
      item.element.classList.remove('is-clock-prepared');
    }

    settleActive() {
      [...this.active].forEach(item => this.finish(item));
    }

    destroy() {
      this.controller.abort();
      clearTimeout(this.fontTimer);
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.settleActive();
      this.items.forEach(item => {
        clearTimeout(item.decodeTimer);
        this.restore(item);
      });
      this.items.clear();
      this.pending.clear();
    }
  };
})();
